import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { FaDownload, FaSpinner, FaSync } from 'react-icons/fa';

const ExportData = () => {
  const { getIdToken } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [error, setError] = useState(null);

  // API URL from environment or default to our deployed endpoint
  const API_URL = process.env.REACT_APP_API_URL || 'https://hqln2txwrk.execute-api.us-east-1.amazonaws.com/dev';

  // Function to initiate data export
  const initiateExport = async () => {
    setIsExporting(true);
    setError(null);
    
    try {
      // Call the export API endpoint - using simplified auth for MVP
      const response = await fetch(`${API_URL}/export`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to initiate export');
      }
      
      const data = await response.json();
      setExportStatus(data);
      
      // If we have an exportId, check status after a delay
      if (data.exportId) {
        setTimeout(() => {
          checkExportStatus(data.exportId);
        }, 2000);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message || 'Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Function to check export status
  const checkExportStatus = async (exportId) => {
    if (!exportId) return;
    
    try {
      // Call the export status API endpoint - using simplified auth for MVP
      const response = await fetch(`${API_URL}/export/${exportId}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to check export status');
      }
      
      const data = await response.json();
      setExportStatus(data);
      
      // If export is still in progress, check again after a delay
      if (data.status === 'in_progress') {
        setTimeout(() => {
          checkExportStatus(exportId);
        }, 3000);
      }
    } catch (err) {
      console.error('Export status check error:', err);
      setError(err.message || 'Failed to check export status. Please try again.');
    }
  };

  // Effect to check export status when component mounts if we have an exportId
  useEffect(() => {
    if (exportStatus && exportStatus.exportId && exportStatus.status === 'in_progress') {
      checkExportStatus(exportStatus.exportId);
    }
  }, []);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Export Your Data</h2>
      <p className="text-gray-600 mb-4">
        Download a ZIP file containing all your albums, songs, moodboards, and associated media files.
        This allows you to back up your data or transfer it to another system.
      </p>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {exportStatus && exportStatus.status === 'in_progress' && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          <div className="flex items-center">
            <FaSpinner className="animate-spin mr-2" />
            <p>Export in progress... This may take a few moments.</p>
          </div>
          <button
            onClick={() => exportStatus.exportId && checkExportStatus(exportStatus.exportId)}
            className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <FaSync className="mr-1" /> Check Status
          </button>
        </div>
      )}
      
      {exportStatus && exportStatus.status === 'completed' ? (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p className="font-semibold">Export Ready!</p>
          <p className="text-sm mb-2">Your data export is ready for download.</p>
          <p className="text-xs mb-3">Link expires: {new Date(exportStatus.expiresAt).toLocaleString()}</p>
          <a 
            href={exportStatus.downloadUrl}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaDownload className="mr-2" />
            Download ZIP
          </a>
        </div>
      ) : (
        <button
          onClick={initiateExport}
          disabled={isExporting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isExporting ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Preparing Export...
            </>
          ) : (
            <>
              <FaDownload className="mr-2" />
              Export All Data
            </>
          )}
        </button>
      )}
      
      <div className="mt-4 text-xs text-gray-500">
        <p>The export includes:</p>
        <ul className="list-disc pl-5 mt-1">
          <li>All your albums and songs data (JSON format)</li>
          <li>All your moodboards and their items</li>
          <li>Audio files for all song versions</li>
          <li>Images for album covers, song covers, and moodboard items</li>
        </ul>
      </div>
    </div>
  );
};

export default ExportData;
